// =============================================================================
// IKIGAI SERVICE - Firestore CRUD for Purpose / Ikigai data
// Path: users/{userId}/ikigai (single document per user)
// =============================================================================

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { IkigaiData, IkigaiAnswers } from './types';

const COLLECTION_NAME = 'ikigai';

function removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined) {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          result[key] = removeUndefinedFields(value as Record<string, unknown>) as T[typeof key];
        } else {
          result[key] = value;
        }
      }
    }
  }
  return result;
}

/**
 * Load the Ikigai document for a user.
 * Returns null if not found.
 */
export async function getIkigai(userId: string): Promise<IkigaiData | null> {
  try {
    const ref = doc(db, COLLECTION_NAME, userId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as IkigaiData;
  } catch (err) {
    console.error('[ikigai.service] getIkigai error:', err);
    return null;
  }
}

/**
 * Save (create or overwrite) the Ikigai document for a user.
 */
export async function saveIkigai(
  userId: string,
  answers: IkigaiAnswers,
  purposeSummary?: string,
): Promise<void> {
  const now = new Date().toISOString();
  const ref = doc(db, COLLECTION_NAME, userId);

  const existing = await getIkigai(userId);

  const data: IkigaiData = {
    userId,
    answers,
    purposeSummary: purposeSummary || '',
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  };

  const clean = removeUndefinedFields(data as unknown as Record<string, unknown>);
  await setDoc(ref, clean, { merge: false });
}

/**
 * Update only the purposeSummary field.
 */
export async function updatePurposeSummary(userId: string, summary: string): Promise<void> {
  const ref = doc(db, COLLECTION_NAME, userId);
  await setDoc(ref, { purposeSummary: summary, updatedAt: new Date().toISOString() }, { merge: true });
}

/**
 * Delete the Ikigai document for a user.
 */
export async function deleteIkigai(userId: string): Promise<void> {
  const ref = doc(db, COLLECTION_NAME, userId);
  await deleteDoc(ref);
}
